import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface NavigationState {
  path: string;
  state: any;
  searchParams?: URLSearchParams;
}

interface NavigationContextType {
  previousState: NavigationState | null;
  setPreviousState: (state: NavigationState) => void;
  goBack: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider = ({ children }: { children: React.ReactNode }) => {
  const [previousState, setPreviousState] = useState<NavigationState | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Store the current state before navigation
    const handleBeforeUnload = () => {
      setPreviousState({
        path: location.pathname,
        state: location.state,
        searchParams: new URLSearchParams(location.search),
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [location]);

  const goBack = () => {
    if (previousState) {
      const search = previousState.searchParams?.toString() || "";
      navigate(`${previousState.path}${search ? `?${search}` : ""}`, {
        state: previousState.state,
      });
    } else {
      navigate(-1);
    }
  };

  return (
    <NavigationContext.Provider value={{ previousState, setPreviousState, goBack }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};
